import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { ExpenseClaim as ECType, ExpenseClaimItem, expenseClaimApi, advanceCashApi, AdvanceCashRequest as ACType, DealAttachment, ADMIN_ROLES, ApprovalEntry } from '@/lib/api'
import { formatDateOnly, formatMyanmarDateTime } from '@/lib/dateUtils'
import SlideOver from '@/components/SlideOver'
import MultiProjectSelect from '@/components/MultiProjectSelect'
import type { ProjectInfo } from '@/components/MultiProjectSelect'
import ContactSearchSelect from '@/components/ContactSearchSelect'
import type { ContactInfo } from '@/components/ContactSearchSelect'
import CommentsSection from '@/components/CommentsSection'
import ApprovalsPanel from '@/components/ApprovalsPanel'
import {
  Plus, Search, AlertCircle, CheckCircle, Clock, XCircle,
  FileText, Image, Upload, X, Receipt, Trash2, FileDown
} from 'lucide-react'

const EXPENSE_TYPES = [
  'Equipment Purchase',
  'Trip',
  'Installation Expense',
  'Labor',
  'Sales Expense',
  'Marketing Expense',
  'Admin Expense',
  'Emergency',
  'Inventory Purchase',
]

const LINKED_TYPES = new Set([
  'Equipment Purchase', 'Trip', 'Installation Expense', 'Labor', 'Sales Expense',
])

const FINANCE_ADMIN = ['super_admin', 'admin', 'finance']

function statusBadge(status: string) {
  if (status === 'Approved') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium"><CheckCircle size={12} />{status}</span>
  if (status === 'Rejected') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium"><XCircle size={12} />{status}</span>
  if (status === 'Disbursed') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium"><CheckCircle size={12} />{status}</span>
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium"><Clock size={12} />{status}</span>
}

function formatAmount(v: number) {
  if (!v) return '0 MMK'
  if (v >= 100000) return `${(v / 100000).toFixed(v % 100000 === 0 ? 0 : 1)} Lakhs MMK`
  return `${v.toLocaleString()} MMK`
}

function formatDate(d: string | null) {
  if (!d) return '—'
  return formatDateOnly(d) || d
}

function formatDateTime(d: string | null) {
  if (!d) return '—'
  return formatMyanmarDateTime(d) || d
}

interface FormItem {
  expense_type: string
  description: string
  amount: string
  project_ids: ProjectInfo[]
  client_id: number | null
  contact_label: string
}

function emptyItem(): FormItem {
  return { expense_type: '', description: '', amount: '', project_ids: [], client_id: null, contact_label: '' }
}

export default function ExpenseClaim() {
  const queryClient = useQueryClient()
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = currentUser.role || ''
  const isFinanceAdmin = FINANCE_ADMIN.includes(userRole)

  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selectedEC, setSelectedEC] = useState<ECType | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [tab, setTab] = useState<'details' | 'items' | 'attachments' | 'comments'>('details')
  const [form, setForm] = useState<any>({})
  const [formItems, setFormItems] = useState<FormItem[]>([emptyItem()])
  const [formError, setFormError] = useState('')
  const [formPhotos, setFormPhotos] = useState<File[]>([])
  const [formDocs, setFormDocs] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState('')
  const [rejectMode, setRejectMode] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const [docError, setDocError] = useState('')
  const photoRef = useRef<HTMLInputElement>(null)
  const docRef = useRef<HTMLInputElement>(null)
  const formPhotoRef = useRef<HTMLInputElement>(null)
  const formDocRef = useRef<HTMLInputElement>(null)

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) {
      api.get(`/expense-claims/${openId}`).then(r => {
        setSelectedEC(r.data)
        setSearchParams({}, { replace: true })
      }).catch(() => setSearchParams({}, { replace: true }))
    }
  }, [])

  const { data: ecs = [], isLoading } = useQuery<ECType[]>({
    queryKey: ['expense-claims', statusFilter],
    queryFn: () => expenseClaimApi.list({ status: statusFilter || undefined }).then(r => r.data),
  })

  const { data: ecSummary } = useQuery({
    queryKey: ['expense-claims-summary'],
    queryFn: () => expenseClaimApi.summary().then(r => r.data),
    enabled: isFinanceAdmin,
  })


  const approveMut = useMutation({
    mutationFn: (id: number) => expenseClaimApi.approve(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
      setSelectedEC(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
    },
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => expenseClaimApi.reject(id, reason),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
      setSelectedEC(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
      setRejectMode(false)
      setRejectReason('')
    },
  })

  const revokeMut = useMutation({
    mutationFn: (id: number) => expenseClaimApi.revokeApproval(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
      setSelectedEC(prev => prev ? { ...prev, status: res.data.status, approvals: res.data.approvals } : prev)
    },
  })

  const deletePhotoMut = useMutation({
    mutationFn: ({ id, photoId }: { id: number; photoId: string }) => expenseClaimApi.deletePhoto(id, photoId),
    onSuccess: (_res, vars) => {
      setSelectedEC(prev => prev ? { ...prev, photos: (prev.photos || []).filter((p: any) => p.id !== vars.photoId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
    },
  })

  const deleteDocMut = useMutation({
    mutationFn: ({ id, docId }: { id: number; docId: string }) => expenseClaimApi.deleteDocument(id, docId),
    onSuccess: (_res, vars) => {
      setSelectedEC(prev => prev ? { ...prev, documents: (prev.documents || []).filter((d: any) => d.id !== vars.docId) } : prev)
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
    },
  })

  const deleteECMut = useMutation({
    mutationFn: (id: number) => expenseClaimApi.deleteEC(id),
    onSuccess: () => {
      setSelectedEC(null)
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
    },
  })

  const [showDisburse, setShowDisburse] = useState(false)
  const [disburseRef, setDisburseRef] = useState('')
  const [claimType, setClaimType] = useState<'Claim' | 'Advance Cash Settlement'>('Claim')
  const [linkedAcId, setLinkedAcId] = useState<number | null>(null)

  const { data: myDisbursedAcs = [] } = useQuery<ACType[]>({
    queryKey: ['my-disbursed-acs', currentUser.id],
    queryFn: () => advanceCashApi.list({ status: 'Disbursed' }).then(r =>
      r.data.filter(ac => ac.submitted_by === currentUser.id)
    ),
    enabled: claimType === 'Advance Cash Settlement' && showCreate,
  })

  const disburseMut = useMutation({
    mutationFn: ({ id, reference }: { id: number; reference?: string }) =>
      expenseClaimApi.disburse(id, reference),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
      setSelectedEC(prev => prev ? { ...prev, status: res.data.status, disbursed_at: res.data.disbursed_at } : prev)
      setShowDisburse(false)
      setDisburseRef('')
    },
  })

  function resetForm() {
    setForm({})
    setFormItems([emptyItem()])
    setFormError('')
    setFormPhotos([])
    setFormDocs([])
    setSubmitStatus('')
    setClaimType('Claim')
    setLinkedAcId(null)
    if (formPhotoRef.current) formPhotoRef.current.value = ''
    if (formDocRef.current) formDocRef.current.value = ''
  }

  function handleFormPhotoAdd(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setFormPhotos(prev => [...prev, ...files].slice(0, 5))
    if (formPhotoRef.current) formPhotoRef.current.value = ''
  }

  function handleFormDocAdd(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setFormDocs(prev => [...prev, ...files].slice(0, 5))
    if (formDocRef.current) formDocRef.current.value = ''
  }

  const filteredECs = (ecs || []).filter(ec => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      ec.ec_number.toLowerCase().includes(q) ||
      ec.title.toLowerCase().includes(q) ||
      (ec.submitted_by_name || '').toLowerCase().includes(q)
    )
  })

  function updateFormItem(idx: number, patch: Partial<FormItem>) {
    setFormItems(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item))
  }

  function removeFormItem(idx: number) {
    if (formItems.length === 1) return
    setFormItems(prev => prev.filter((_, i) => i !== idx))
  }

  const totalAmount = formItems.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError('')
    if (!form.title?.trim()) { setFormError('Please enter a title'); return }

    const validItems = formItems.filter(i => i.description.trim() || i.expense_type)
    if (validItems.length === 0) { setFormError('Please add at least one expense item'); return }

    for (let idx = 0; idx < validItems.length; idx++) {
      const item = validItems[idx]
      if (!item.expense_type) { setFormError(`Item ${idx + 1}: please select an expense type`); return }
      if (!item.description.trim()) { setFormError(`Item ${idx + 1}: description is required`); return }
      const amt = parseFloat(item.amount)
      if (!amt || amt <= 0) { setFormError(`Item ${idx + 1}: amount must be greater than 0`); return }
      if (LINKED_TYPES.has(item.expense_type) && item.project_ids.length === 0 && !item.client_id) {
        setFormError(`Item ${idx + 1} (${item.expense_type}): requires at least one linked project or a contact`)
        return
      }
    }

    if (claimType === 'Advance Cash Settlement' && !linkedAcId) {
      setFormError('Please select an Advance Cash request to settle against')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(formPhotos.length > 0 || formDocs.length > 0 ? 'Submitting claim with attachments...' : 'Submitting claim...')

    try {
      await expenseClaimApi.create({
        title: form.title.trim(),
        additional_approvers: form.additional_approvers || [],
        requested_date: form.requested_date || null,
        notes: form.notes?.trim() || null,
        items: validItems.map((item, idx) => ({
          expense_type: item.expense_type,
          description: item.description.trim(),
          amount: parseFloat(item.amount),
          project_id: item.project_ids.length > 0 ? item.project_ids[0].id : null,
          client_id: item.client_id || null,
          project_ids: item.project_ids.map(p => p.id),
          sort_order: idx,
        })),
        photos: formPhotos,
        documents: formDocs,
        claim_type: claimType,
        linked_ac_id: claimType === 'Advance Cash Settlement' ? linkedAcId : null,
      })
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
      setShowCreate(false)
      resetForm()
    } catch (err: any) {
      const status = err.response?.status
      const detail = err.response?.data?.detail
      if (detail) {
        setFormError(detail)
      } else if (status === 403 || status === 401) {
        setFormError('Your session has expired. Please log out and log back in, then try again.')
      } else if (status === 503) {
        setFormError('File storage is temporarily unavailable. Remove any attachments and try again, or contact an administrator.')
      } else if (status === 502) {
        setFormError('Failed to upload attachment to storage. Please try again.')
      } else if (!err.response) {
        setFormError('No connection to server. Please check your internet connection and try again.')
      } else {
        setFormError(`Submission failed (server error ${status ?? 'unknown'}). Please try again or contact support.`)
      }
    } finally {
      setIsSubmitting(false)
      setSubmitStatus('')
    }
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedEC) return
    setPhotoError('')
    setUploadingPhoto(true)
    try {
      const res = await expenseClaimApi.uploadPhoto(selectedEC.id, file)
      setSelectedEC(prev => prev ? { ...prev, photos: [...(prev.photos || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
      if (photoRef.current) photoRef.current.value = ''
    }
  }

  async function handleDocUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !selectedEC) return
    setDocError('')
    setUploadingDoc(true)
    try {
      const res = await expenseClaimApi.uploadDocument(selectedEC.id, file)
      setSelectedEC(prev => prev ? { ...prev, documents: [...(prev.documents || []), res.data.file] } : prev)
      queryClient.invalidateQueries({ queryKey: ['expense-claims'] })
    } catch (err: any) {
      setDocError(err.response?.data?.detail || 'Failed to upload document')
    } finally {
      setUploadingDoc(false)
      if (docRef.current) docRef.current.value = ''
    }
  }

  const canUpload = selectedEC
    ? (isFinanceAdmin || selectedEC.submitted_by === currentUser.id)
    : false

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Receipt size={22} className="text-red-800" />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Expense Claims</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            try {
              const res = await api.get('/export/expense-claims', { params: { status: statusFilter, search }, responseType: 'blob' })
              const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = 'expense_claims.xlsx'; a.click(); URL.revokeObjectURL(url)
            } catch {}
          }} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600" title="Export to Excel">
            <FileDown size={15} /> <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => { resetForm(); setShowCreate(true) }}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 min-h-[44px]"
          >
            <Plus size={16} /> New Claim
          </button>
        </div>
      </div>

      {/* Finance Summary Strip */}
      {isFinanceAdmin && ecSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Pending', count: ecSummary.count_pending, total: ecSummary.total_pending, color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
            { label: 'Approved', count: ecSummary.count_approved, total: ecSummary.total_approved, color: 'bg-green-50 border-green-200 text-green-700' },
            { label: 'Disbursed', count: ecSummary.count_disbursed, total: ecSummary.total_disbursed, color: 'bg-teal-50 border-teal-200 text-teal-700' },
            { label: 'Rejected', count: ecSummary.count_rejected, total: ecSummary.total_rejected, color: 'bg-red-50 border-red-200 text-red-700' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border p-3 ${s.color}`}>
              <div className="text-xs font-medium opacity-80">{s.label}</div>
              <div className="text-lg font-bold mt-0.5">{s.count}</div>
              <div className="text-xs opacity-70">{formatAmount(s.total)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input
            type="text"
            placeholder="Search by number, title, submitter..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Disbursed">Disbursed</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800" />
          </div>
        ) : filteredECs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
            <Receipt size={40} strokeWidth={1} />
            <p className="text-sm">No expense claims found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">EC #</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-600 text-xs uppercase tracking-wider">Total</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Submitted By</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredECs.map(ec => (
                  <tr
                    key={ec.id}
                    onClick={async () => {
                      const full = await expenseClaimApi.get(ec.id)
                      setSelectedEC(full.data)
                      setTab('details')
                      setRejectMode(false)
                      setPhotoError('')
                      setDocError('')
                    }}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-red-900">{ec.ec_number}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 max-w-[200px] truncate">{ec.title}</td>
                    <td className="px-4 py-3">
                      {ec.claim_type === 'Advance Cash Settlement' ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">AC Settlement</span>
                          {ec.linked_ac_number && (
                            <span className="text-xs text-slate-500">{ec.linked_ac_number}</span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">Claim</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatAmount(Number(ec.total_amount))}</td>
                    <td className="px-4 py-3 text-slate-600">{ec.submitted_by_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(ec.requested_date || ec.created_at)}</td>
                    <td className="px-4 py-3">{statusBadge(ec.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create SlideOver */}
      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); resetForm() }} title="New Expense Claim" wide>
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {formError && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
              <AlertCircle size={16} className="shrink-0" /><span>{formError}</span>
            </div>
          )}

          {/* Claim Type Toggle */}
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Claim Type *</label>
            <div className="flex rounded-lg border border-slate-300 overflow-hidden text-sm">
              {(['Claim', 'Advance Cash Settlement'] as const).map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => { setClaimType(opt); setLinkedAcId(null) }}
                  className={`flex-1 py-2 px-3 font-medium transition-colors ${
                    claimType === opt
                      ? 'bg-red-700 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Advance Cash selection (only when AC Settlement selected) */}
          {claimType === 'Advance Cash Settlement' && (
            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Link to Advance Cash Request *</label>
                {myDisbursedAcs.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No disbursed Advance Cash requests found for your account.</p>
                ) : (
                  <select
                    value={linkedAcId ?? ''}
                    onChange={e => setLinkedAcId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
                    required
                  >
                    <option value="">-- Select Advance Cash Request --</option>
                    {myDisbursedAcs.map(ac => (
                      <option key={ac.id} value={ac.id}>
                        {ac.ac_number} — {ac.title} (MMK {ac.amount.toLocaleString()})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {/* Balance preview for the selected AC */}
              {linkedAcId && (() => {
                const selAc = myDisbursedAcs.find(ac => ac.id === linkedAcId)
                if (!selAc) return null
                const disbursed = Number(selAc.amount)
                const settled = Number(selAc.settled_amount ?? 0)
                const remaining = disbursed - settled
                return (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-xs font-semibold text-slate-600 mb-1.5">{selAc.ac_number} — Balance</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Disbursed', value: disbursed, color: 'text-teal-700' },
                        { label: 'Settled', value: settled, color: 'text-blue-700' },
                        { label: 'Remaining', value: remaining, color: 'text-orange-700 font-bold' },
                      ].map(item => (
                        <div key={item.label} className="text-center">
                          <p className="text-xs text-slate-500">{item.label}</p>
                          <p className={`text-xs mt-0.5 ${item.color}`}>MMK {item.value.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Title *</label>
            <input
              type="text"
              value={form.title || ''}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Brief description of the expense claim"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Requested Date</label>
            <input
              type="date"
              value={form.requested_date || ''}
              onChange={e => setForm({ ...form, requested_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
            />
          </div>

          {/* Expense Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Expense Items *</label>
              <button type="button" onClick={() => setFormItems(prev => [...prev, emptyItem()])}
                className="text-xs text-red-800 hover:text-red-900 font-medium flex items-center gap-1">
                <Plus size={12} /> Add Row
              </button>
            </div>
            <div className="space-y-3">
              {formItems.map((item, idx) => {
                const needsLink = LINKED_TYPES.has(item.expense_type)
                return (
                  <div key={idx} className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 font-medium w-5 shrink-0">{idx + 1}.</span>
                      <select
                        value={item.expense_type}
                        onChange={e => updateFormItem(idx, { expense_type: e.target.value, project_ids: [], client_id: null, contact_label: '' })}
                        className="flex-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
                      >
                        <option value="">Type...</option>
                        {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      {formItems.length > 1 && (
                        <button type="button" onClick={() => removeFormItem(idx)}
                          className="p-1.5 text-slate-400 hover:text-red-600 shrink-0">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 ml-7">
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => updateFormItem(idx, { description: e.target.value })}
                        placeholder="Description"
                        className="flex-1 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
                      />
                      <div className="w-32 shrink-0">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={item.amount}
                          onChange={e => updateFormItem(idx, { amount: e.target.value })}
                          placeholder="Amount"
                          className="w-full px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
                        />
                      </div>
                    </div>
                    {needsLink && (
                      <div className="ml-7 space-y-2">
                        {item.project_ids.length === 0 && !item.client_id && (
                          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            Requires at least one linked project or contact
                          </div>
                        )}
                        <MultiProjectSelect
                          selected={item.project_ids}
                          onAdd={(p: ProjectInfo) => updateFormItem(idx, {
                            project_ids: item.project_ids.find(x => x.id === p.id) ? item.project_ids : [...item.project_ids, p],
                          })}
                          onRemove={(id: number) => updateFormItem(idx, { project_ids: item.project_ids.filter(p => p.id !== id) })}
                          label="Linked Projects"
                        />
                        <ContactSearchSelect
                          value={item.contact_label}
                          onSelect={(c: ContactInfo) => updateFormItem(idx, { client_id: c.id, contact_label: c.company ? `${c.client_name} — ${c.company}` : c.client_name })}
                          onClear={() => updateFormItem(idx, { client_id: null, contact_label: '' })}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end mt-2 px-1">
              <span className="text-sm font-semibold text-slate-700">
                Total: <span className="text-red-800">{formatAmount(totalAmount)}</span>
              </span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Notes</label>
            <textarea
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Additional details..."
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 resize-none"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Additional Approvers</label>
            <p className="text-xs text-slate-500 mb-2">If none selected, the claim is automatically routed to the Finance &amp; Admin team for approval.</p>
            <div className="space-y-2">
              {['aungthanzaw', 'tuntunwin', 'thwinhtooaung'].map(username => {
                const selected: string[] = form.additional_approvers || []
                return (
                  <label key={username} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={selected.includes(username)}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...selected, username]
                          : selected.filter(u => u !== username)
                        setForm({ ...form, additional_approvers: next })
                      }}
                      className="w-4 h-4 accent-red-800"
                    />
                    <span className="text-sm font-medium text-slate-800">{username}</span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Form Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Photos (optional, max 5)</label>
              {formPhotos.length < 5 && (
                <label className="text-xs text-red-800 hover:text-red-900 font-medium flex items-center gap-1 cursor-pointer">
                  <Plus size={12} /> Add Photo
                  <input ref={formPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFormPhotoAdd} />
                </label>
              )}
            </div>
            {formPhotos.length > 0 && (
              <div className="space-y-1">
                {formPhotos.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 text-sm">
                    <Image size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                    <button type="button" onClick={() => setFormPhotos(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600 shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Documents */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Documents (optional, max 5)</label>
              {formDocs.length < 5 && (
                <label className="text-xs text-red-800 hover:text-red-900 font-medium flex items-center gap-1 cursor-pointer">
                  <FileText size={12} /> Add Document
                  <input ref={formDocRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" multiple className="hidden" onChange={handleFormDocAdd} />
                </label>
              )}
            </div>
            {formDocs.length > 0 && (
              <div className="space-y-1">
                {formDocs.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 text-sm">
                    <FileText size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                    <button type="button" onClick={() => setFormDocs(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600 shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {submitStatus && (
            <div className="flex items-center gap-2 bg-blue-50 text-blue-700 text-sm px-4 py-3 rounded-lg border border-blue-200">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700 shrink-0" />
              <span>{submitStatus}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting}
              className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
              {isSubmitting ? 'Submitting...' : 'Submit Claim'}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); resetForm() }} disabled={isSubmitting}
              className="py-2 px-4 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </form>
      </SlideOver>

      {/* Detail SlideOver */}
      <SlideOver
        open={!!selectedEC}
        onClose={() => { setSelectedEC(null); setRejectMode(false); setPhotoError(''); setDocError('') }}
        title={selectedEC ? `${selectedEC.ec_number} — ${selectedEC.title}` : ''}
        wide
      >
        {selectedEC && (
          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              {statusBadge(selectedEC.status)}
              <div className="flex items-center gap-2">
                {selectedEC.status === 'Approved' && isFinanceAdmin && !showDisburse && (
                  <button
                    onClick={() => setShowDisburse(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-700 border border-teal-300 rounded-lg hover:bg-teal-50"
                  >
                    <CheckCircle size={13} /> Mark as Disbursed
                  </button>
                )}
                {selectedEC.status === 'Pending' && (isFinanceAdmin || selectedEC.submitted_by === currentUser.id) && (
                  <button
                    onClick={() => { if (confirm('Delete this expense claim? This cannot be undone.')) deleteECMut.mutate(selectedEC.id) }}
                    disabled={deleteECMut.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 size={13} /> {deleteECMut.isPending ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>

            {/* Disburse inline form */}
            {showDisburse && selectedEC.status === 'Approved' && isFinanceAdmin && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-teal-800">Mark as Disbursed</p>
                <input
                  type="text"
                  value={disburseRef}
                  onChange={e => setDisburseRef(e.target.value)}
                  placeholder="Reference / Voucher number (optional)"
                  className="w-full px-3 py-2 text-sm border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => disburseMut.mutate({ id: selectedEC.id, reference: disburseRef || undefined })}
                    disabled={disburseMut.isPending}
                    className="flex-1 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                  >
                    {disburseMut.isPending ? 'Disbursing…' : 'Confirm Disbursement'}
                  </button>
                  <button
                    onClick={() => { setShowDisburse(false); setDisburseRef('') }}
                    className="py-2 px-4 text-sm bg-white border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-50"
                  >
                    Cancel
                  </button>
                </div>
                {disburseMut.isError && (
                  <p className="text-xs text-red-600">{(disburseMut.error as any)?.response?.data?.detail || 'Failed to disburse'}</p>
                )}
              </div>
            )}

            {/* Approvals Panel */}
            <ApprovalsPanel
              approvals={selectedEC.approvals || []}
              additionalApprovers={selectedEC.additional_approvers || []}
              currentUsername={currentUser.username || ''}
              currentRole={userRole}
              isPending={selectedEC.status === 'Pending'}
              rejectMode={rejectMode}
              rejectReason={rejectReason}
              onRejectReasonChange={setRejectReason}
              onApprove={() => approveMut.mutate(selectedEC.id)}
              onReject={(reason) => rejectMut.mutate({ id: selectedEC.id, reason })}
              onRevoke={() => revokeMut.mutate(selectedEC.id)}
              onToggleRejectMode={() => setRejectMode(r => !r)}
              onCancelReject={() => { setRejectMode(false); setRejectReason('') }}
              isApprovePending={approveMut.isPending}
              isRejectPending={rejectMut.isPending}
              isRevokePending={revokeMut.isPending}
            />

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200">
              {(['details', 'items', 'attachments', 'comments'] as const).map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${tab === t ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500'}`}>
                  {t}
                </button>
              ))}
            </div>

            {tab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Total Amount', formatAmount(Number(selectedEC.total_amount))],
                    ['Requested Date', formatDate(selectedEC.requested_date)],
                    ['Submitted By', selectedEC.submitted_by_name || '—'],
                    ['Submitted At', formatDateTime(selectedEC.created_at)],
                    ['Additional Approvers', (selectedEC.additional_approvers || []).length > 0 ? (selectedEC.additional_approvers || []).join(', ') : '—'],
                  ].map(([label, value]) => (
                    <div key={label as string} className="text-sm">
                      <span className="text-slate-500 text-xs block">{label}</span>
                      <span className="font-medium text-slate-900">{value}</span>
                    </div>
                  ))}
                </div>
                {/* Claim Type Info */}
                {selectedEC.claim_type === 'Advance Cash Settlement' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <span className="text-blue-700 text-xs block font-medium mb-1">Advance Cash Settlement</span>
                    <span className="text-xs text-slate-600">
                      This claim is linked to {selectedEC.linked_ac_number ?? `AC-${String(selectedEC.linked_ac_id).padStart(4, '0')}`} for settlement.
                    </span>
                  </div>
                )}

                {selectedEC.disbursed_at && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                    <span className="text-teal-600 text-xs block font-medium mb-1">Disbursement Info</span>
                    <div className="flex flex-wrap gap-4">
                      <span className="text-xs text-slate-600">Date: <strong className="text-slate-800">{formatDateTime(selectedEC.disbursed_at)}</strong></span>
                      {selectedEC.disbursement_reference && (
                        <span className="text-xs text-slate-600">Ref: <strong className="text-slate-800">{selectedEC.disbursement_reference}</strong></span>
                      )}
                    </div>
                  </div>
                )}
                {selectedEC.notes && (
                  <div>
                    <span className="text-xs text-slate-500 block mb-1">Notes</span>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-200">{selectedEC.notes}</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'items' && (
              <div className="space-y-2">
                {(selectedEC.items || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No items found</p>
                ) : (
                  <>
                    {(selectedEC.items || []).map((item, idx) => {
                      const itemProjectIds = item.project_ids ?? []
                      const contactLabel = item.clients
                        ? (item.clients.company ? `${item.clients.client_name} — ${item.clients.company}` : item.clients.client_name)
                        : null
                      return (
                        <div key={item.id ?? idx} className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-sm">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 mb-1">{item.expense_type}</span>
                              <p className="font-medium text-slate-900">{item.description}</p>
                              {itemProjectIds.length > 0 ? (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {itemProjectIds.map(p => (
                                    <span key={p.id} className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-red-50 text-red-800 border border-red-200 rounded-full">
                                      {p.project_type}-{p.project_code}
                                    </span>
                                  ))}
                                </div>
                              ) : item.projects ? (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Project: {item.projects.project_type}-{item.projects.project_code}
                                </p>
                              ) : null}
                              {contactLabel && <p className="text-xs text-slate-500 mt-0.5">Contact: {contactLabel}</p>}
                            </div>
                            <span className="font-semibold text-slate-900 whitespace-nowrap">{formatAmount(Number(item.amount))}</span>
                          </div>
                        </div>
                      )
                    })}
                    <div className="flex justify-end pt-1">
                      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm font-semibold text-red-900">
                        Total: {formatAmount(Number(selectedEC.total_amount))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === 'attachments' && (
              <div className="space-y-4">
                {/* Photos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-slate-700 flex items-center gap-1"><Image size={14} /> Photos</h4>
                    {canUpload && (
                      <label className={`flex items-center gap-1 text-xs text-red-800 hover:text-red-900 cursor-pointer ${uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
                        <Upload size={12} /> {uploadingPhoto ? 'Uploading...' : 'Upload'}
                        <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                      </label>
                    )}
                  </div>
                  {photoError && <p className="text-xs text-red-600 mb-2">{photoError}</p>}
                  {(selectedEC.photos || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No photos uploaded</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {(selectedEC.photos || []).map((photo: DealAttachment) => (
                        <div key={photo.id} className="relative group">
                          <a href={expenseClaimApi.getFileSrc(photo, selectedEC.id)} target="_blank" rel="noreferrer">
                            <img
                              src={expenseClaimApi.getFileSrc(photo, selectedEC.id)}
                              alt={photo.name}
                              className="w-full h-24 object-cover rounded-lg border border-slate-200"
                            />
                          </a>
                          {canUpload && (
                            <button
                              onClick={() => deletePhotoMut.mutate({ id: selectedEC.id, photoId: photo.id })}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <X size={10} />
                            </button>
                          )}
                          <p className="text-xs text-slate-500 truncate mt-1">{photo.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-slate-700 flex items-center gap-1"><FileText size={14} /> Documents</h4>
                    {canUpload && (
                      <label className={`flex items-center gap-1 text-xs text-red-800 hover:text-red-900 cursor-pointer ${uploadingDoc ? 'opacity-50 pointer-events-none' : ''}`}>
                        <Upload size={12} /> {uploadingDoc ? 'Uploading...' : 'Upload'}
                        <input ref={docRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" className="hidden" onChange={handleDocUpload} disabled={uploadingDoc} />
                      </label>
                    )}
                  </div>
                  {docError && <p className="text-xs text-red-600 mb-2">{docError}</p>}
                  {(selectedEC.documents || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No documents uploaded</p>
                  ) : (
                    <div className="space-y-2">
                      {(selectedEC.documents || []).map((doc: DealAttachment) => (
                        <div key={doc.id} className="flex items-center justify-between gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg">
                          <a href={expenseClaimApi.getFileSrc(doc, selectedEC.id)} target="_blank" rel="noreferrer"
                            className="flex items-center gap-2 text-xs text-blue-700 hover:underline truncate">
                            <FileText size={14} className="shrink-0" />
                            <span className="truncate">{doc.name}</span>
                          </a>
                          {canUpload && (
                            <button onClick={() => deleteDocMut.mutate({ id: selectedEC.id, docId: doc.id })}
                              className="p-1 text-slate-400 hover:text-red-600 shrink-0">
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'comments' && (
              <CommentsSection entityType="expense_claim" entityId={selectedEC.id} />
            )}
          </div>
        )}
      </SlideOver>
    </div>
  )
}
